import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS } from 'constants/businessToolSections/forBank';
import {
  FOR_INVESTORS_SECTIONS,
  FOR_INVESTORS_SUB_SECTIONS,
} from 'constants/businessToolSections/forInvestors';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { SalesAndPlannedProducts } from 'components/BusinessToolForm/Sections/MarketingAndSales/SalesPlanSection/components/SalesAndPlannedProducts';
import { SalesChannels } from 'components/BusinessToolForm/Sections/MarketingAndSales/SalesPlanSection/components/SalesChannels';
import {
  channelsInitialValues,
  prefix,
  SALES_NAMES,
} from 'components/BusinessToolForm/Sections/MarketingAndSales/SalesPlanSection/utils/form';

import { concatWithDot } from 'helpers/string';

export const SalesPlan: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_INVESTORS_SUB_SECTIONS.FOR_INVESTORS_MARKETING_AND_SALES.SALES_PLAN
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('marketingAndSales.title')}
          badgeText={t('marketingAndSales.sales.title')}
          section={FOR_INVESTORS_SECTIONS.MARKETING_AND_SALES}
        />
      </FormSectionCard>
      {/* 5.1.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketingAndSales.sales.goals')}
        aiFieldName={concatWithDot(prefix, SALES_NAMES.GOALS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, SALES_NAMES.GOALS)}
          name={concatWithDot(prefix, SALES_NAMES.GOALS)}
          minRows={3}
          disabled={!canAccess}
        />
      </FormSectionCard>
      {/* 5.1.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketingAndSales.sales.affectFactors')}
        aiFieldName={concatWithDot(prefix, SALES_NAMES.AFFECT_FACTORS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, SALES_NAMES.AFFECT_FACTORS)}
          name={concatWithDot(prefix, SALES_NAMES.AFFECT_FACTORS)}
          minRows={3}
          disabled={!canAccess}
        />
      </FormSectionCard>
      {/* 5.1.5 */}
      <SalesAndPlannedProducts disabled={!canAccess} />
      {/* 5.1.6 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketingAndSales.sales.channels.title')}
        collapsible
        aiFieldName={concatWithDot(prefix, SALES_NAMES.CHANNELS)}
        aiFieldType="complex"
      >
        <FieldArrayComponent
          name={concatWithDot(prefix, SALES_NAMES.CHANNELS)}
          initialItem={channelsInitialValues}
          buttonText={t('marketingAndSales.sales.channels.buttonText')}
          disabled={!canAccess}
        >
          {(namePrefix) => <SalesChannels name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 5.1.7 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketingAndSales.sales.detailedSalesChannel')}
        aiFieldName={concatWithDot(prefix, SALES_NAMES.DETAILED_SALES_CHANNEL)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, SALES_NAMES.DETAILED_SALES_CHANNEL)}
          name={concatWithDot(prefix, SALES_NAMES.DETAILED_SALES_CHANNEL)}
          minRows={3}
          disabled={!canAccess}
        />
      </FormSectionCard>
    </>
  );
};
